<template>
  <div class="search-card card" @click="redirectToDetailPage">
    <div class="card-image">
      <img v-if="imageSrc" :src="imageSrc" class="card-img-top" @error="showErrorImage">
      <img v-else class="card-img-top" src="../../assets/app_icon_512.png" alt="No home photo to show">
      <font-awesome-icon
          v-if="canShareOnSocial && userIsAdmin"
          class="share-icon"
          v-tooltip="'This home is safe to share on social media.'"
          icon="bullhorn"
      />
      <div v-if="!isPublic" class="card-img-overlay">
        <div class="overlay-text">
          <h5>Home is Hidden</h5>
          <span>This home has been hidden- only you can see it. Click </span>
          <font-awesome-icon :icon="eyeIcon"/>
          <span> to make it visible to other users.</span>
        </div>
      </div>
    </div>
    <div class="row card-body">
      <div class="col-12">
              <span class="card-title-container">
                <span v-if="titleTooltip" class="tooltip-text">{{ titleTooltip }}</span>
                <h4 ref="cardTitle" :class="['card-title', {'no-name': !homeHasName}]">{{ homeName }}</h4>
              </span>
        <p class="card-text">{{ locationText }} ({{ cardContent }})</p>
      </div>
      <div v-if="userOwnsHome" class="col-12 edit-container">
        <button class="btn btn-primary btn-sm edit" title="Edit" @click.stop="editClicked">
          <font-awesome-icon icon="edit"/>
          <span class="edit-label">Edit</span>
        </button>
        <button class="btn btn-primary btn-sm edit" :title="showOrHide" @click.stop="toggleShow">
          <font-awesome-icon :icon="eyeIcon"/>
          <span class="edit-label">{{ showOrHide }}</span>
        </button>
      <button class="btn btn-danger btn-sm edit" title="Delete" @click.stop="showDeletePopup">
          <font-awesome-icon icon="trash"/>
          <span class="edit-label">Delete</span>
        </button>
      </div>
    </div>
    <Alert
        v-if="deleteError"
        :title="'Something went wrong while deleting. Please try again.'"
        :alert-type="'danger'"
        floating
        @click.stop
    />
    <Modal
        v-if="isModalVisible"
        :primary-action="modalButtonText"
        @close="closeModal"
        @confirm="confirmClicked"
        @click.stop
    >
      <template #header>
        <h3 class="modal-title">{{ modalTitle }}</h3>
      </template>
      <template #body>
        <p class="modal-body">{{ modalText }}</p>
      </template>
    </Modal>
  </div>
</template>

<script>
import {getS3Url, stringFormatter, getImgErrorUrl, sendGoogleAnalyticsEvent, submitStatus} from '../../helpers/utils'
import {mapGetters} from 'vuex';
import {mapActions} from 'vuex';
import Modal from '@/components/Modal.vue';
import Alert from '@/components/Alert.vue';

export default {
  name: "SearchResultCard",
  components: {Modal, Alert},
  data() {
    return {
      placeholderUrl: "/img/app_icon_512.42c7de1c.png",
      titleTooltip: null,
      isPublic: true,
      justToggledVisible: false,
      isModalVisible: false,
      modalTitle: null,
      modalText: null,
      modalAction: null,
      modalArgs: null,
      modalButtonText: null,
      deleteError: false,
      isDeletingFromThisCard: false,
    }
  },
  props: {
    home: {
      type: Object, default: () => {}
    }
  },
  computed: {
    ...mapGetters(['userWithSettings', 'usersWhoConsentedToSharing', 'homeDeleteStatus']),
    user() {
      return this.userWithSettings;
    },
    userOwnsHome() {
      return this.user && [this.user.user_id, this.user.sub].includes(this.home.owner_id);
    },
    userIsAdmin() {
      return this.user && this.user.is_admin;
    },
    imageSrc() {
      const imgToLoad = this.home.photos && this.home.photos[0] ? getS3Url(this.home.photos[0]) : null;
      return imgToLoad;
    },
    locationText() {
      const {home} = this;

      let text = stringFormatter.toSentenceCase(home.country);
      if (home.gad && home.gad.city) {
        text = `${stringFormatter.toSentenceCase(home.gad.city)}, ` + text;
      }
      return text;
    },
    cardContent() {
      return this.yearIsNotEmpty ? this.home.structure.year : 'Year Unknown';
    },
    yearIsNotEmpty() {
      const {home} = this;
      return home.structure && home.structure.year && home.structure.year !== -1;
    },
    homeHasName() {
      return this.homeName !== 'Unnamed Home';
    },
    homeName() {
      const { home } = this;
      let homeName = null;

      if (home.structure.structName){
        homeName = home.structure.structName;
      }
      else if (home.occupants.length > 0) {
        const familyName = home.occupants[0].lName;
        homeName = familyName ? `${stringFormatter.toSentenceCase(familyName)} Family Home` : null;
      }
      return homeName || 'Unnamed Home'
    },
    showOrHide() {
      return this.isPublic ? 'Hide' : 'Show';
    },
    eyeIcon() {
      return this.isPublic ? 'eye-slash' : 'eye';
    },
    canShareOnSocial() {
      const shareableIds = this.usersWhoConsentedToSharing || [];
      return shareableIds.includes(this.home.owner_id);
    }
  },
  mounted() {
    this.setTooltip();
    this.isPublic = !this.home.isHidden;

    if(this.user && this.user.is_admin && !this.usersWhoConsentedToSharing){
      // fetch sharing settings if applicable and we haven't already
      this.getUsersWhoConsentedToSharing();
    }
  },
  updated() {
    this.setTooltip();
    if (!this.justToggledVisible) {
      this.isPublic = !this.home.isHidden;
    } else {
      this.justToggledVisible = false;
    }
  },
  methods: {
    ...mapActions(['updateHome', 'setHomeToEdit', 'getUsersWhoConsentedToSharing', 'deleteHome', 'deletePhoto']),
    redirectToDetailPage() {
      sendGoogleAnalyticsEvent('Home Card Clicked', 'view_item', `from: search page, home_id: ${this.home._id}`);
      this.$router.push({name: 'details', params: {id: this.home._id}});
    },
    editClicked() {
      // redirect to add page, but autofill fields
      sendGoogleAnalyticsEvent('Edit Home', 'edit_item', this.home._id);
      this.setHomeToEdit(this.home);
      this.$router.push({name: 'add', query: { edit: this.home._id }});
    },
    showErrorImage(event) {
      event.target.src = getImgErrorUrl();
    },
    isElementOverflow(element) {
      return element.scrollWidth > element.clientWidth;
    },
    setTooltip() {
      if (this.isElementOverflow(this.$refs.cardTitle)) {
        this.titleTooltip = this.homeName;
      }
    },
    toggleShow() {
      this.isPublic = !this.isPublic;
      this.updateHome({data: {isHidden: !this.isPublic}, id: this.home._id});
      this.justToggledVisible = true;
    },
    handleShowModal(details) {
      const {title, text, action, args, confirmButtonText} = details;
      this.modalTitle = title;
      this.modalText = text;
      this.modalAction = action;
      this.modalArgs = args;
      this.modalButtonText = confirmButtonText;
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
      this.modalTitle = null;
      this.modalText = null;
      this.modalAction = null;
      this.modalArgs = null;
      this.modalButtonText = null;
    },
    confirmClicked() {
      this.modalAction(this.modalArgs);
      this.closeModal();
    },
    showDeletePopup() {
      this.handleShowModal({
        title: 'Confirmation',
        text: 'Are you sure you want to delete this home? Once complete, this action cannot be undone.',
        action: this.confirmDelete,
        args: this.home._id,
        confirmButtonText: 'Delete',
      });
    },
    confirmDelete(id) {
      this.isDeletingFromThisCard = true;
      this.deleteHome(id);
    },
  },
  watch: {
    homeDeleteStatus(to) {
      if (!this.isDeletingFromThisCard) return;
      if (to === submitStatus.SUCCESS) {
        if (this.home.photos) {
          this.home.photos.forEach(photoName => this.deletePhoto(photoName));
        }
        if (this.home.censuses) {
          this.home.censuses.forEach(census => {
            if (census.pdfLinks) {
              census.pdfLinks.forEach(link => this.deletePhoto(link));
            }
          });
        }
        this.isDeletingFromThisCard = false;
      } else if (to === submitStatus.FAILED) {
        this.deleteError = true;
        this.isDeletingFromThisCard = false;
        setTimeout(() => { this.deleteError = false; }, 7000);
      }
    },
  },
}
</script>

<style scoped>
.search-card {
  margin: 0.75rem;
  transition: all .3s ease-out;
}

.search-card:hover {
  cursor: pointer;
  transform: translate(0, -5px);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.card-title {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.tooltip-text {
  bottom: 4rem;
  right: 0;
  width: 100%;
}

.card-title-container:hover .tooltip-text {
  visibility: visible;
}

.no-name {
  color: darkgrey;
  font-style: italic;
  font-weight: lighter;
}

.edit {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.6rem;
}

.edit-label {
  max-width: 0;
  margin-left: 0;
  overflow: hidden;
  white-space: nowrap;
  opacity: 0;
  transition: max-width 0.25s ease, margin-left 0.25s ease, opacity 0.25s ease;
}

.edit:hover .edit-label,
.edit:focus-visible .edit-label {
  max-width: 6rem;
  margin-left: 0.35rem;
  opacity: 1;
}

.edit-container {
  display: flex;
  margin-top: 1rem;
  justify-content: flex-end;
  gap: 0.5rem;
}

.card-image {
  position: relative;
}

.share-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: x-large;
  color: #FFFFFF;
}

.card-img-overlay {
  background-color: rgba(33, 33, 33, 0.7);
  color: white;
  text-align: center;
  display: flex;
  align-items: center;
}

img {
  height: 25rem;
  object-fit: cover;
}

@media screen and (max-width: 600px) {
  img {
    height: 12rem;
  }
}
</style>